/**
 * BaseModal — Layer 1
 * Handles portal rendering, backdrop, responsive shell (mobile/desktop),
 * drag-to-dismiss, body scroll lock, ESC key, ARIA attributes, and
 * dynamic sidebar offset.
 *
 * Sidebar offset: Tries useLayout() (AppLayout routes). If LayoutProvider
 * is absent (AdminSidebarLayout routes), falls back to '8rem'.
 */

import { useEffect, useCallback, useRef, forwardRef, createContext, useContext, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence, animate, useMotionValue, useTransform } from 'motion/react';

// Context so child layers (FormModal, DetailModal) can trigger animated close
const ModalCloseContext = createContext<(() => void) | null>(null);
export function useModalClose() {
  return useContext(ModalCloseContext);
}
import useMediaQuery, { MD_BREAKPOINT } from '@/hooks/useMediaQuery';
import { AnimatedGradientBackground } from '@/components/glass';
import { Z_INDEX, SPRING } from './constants';
import { platform } from '@/utils/platform';

// Both AppLayout and AdminSidebarLayout render a fixed `<aside>` sidebar.
// Rather than depending on LayoutProvider (absent in admin routes), we
// measure the sidebar DOM element directly for pixel-accurate offset.
function useSidebarWidth(isMobile: boolean): string {
  if (isMobile) return '0px';
  if (typeof document === 'undefined') return '8rem';
  const sidebar = document.querySelector('aside.fixed') as HTMLElement | null;
  if (sidebar) return `${sidebar.offsetWidth}px`;
  return '8rem';
}

// Native iOS-calibrated spring — critically damped, no bounce
const NATIVE_SPRING = { type: 'spring' as const, stiffness: 300, damping: 30, mass: 1, restDelta: 0.5 };
// Smooth exit spring — slightly slower for a graceful slide-away on X close
const EXIT_SPRING = { type: 'spring' as const, stiffness: 200, damping: 28, mass: 1 };

interface BaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  preventClose?: boolean;
  mobileVariant?: 'fullscreen' | 'bottomsheet';
  maxWidth?: string;
  ariaLabel?: string;
  onExitComplete?: () => void;
  /** Remove built-in top padding so content can extend to the top edge */
  flushTop?: boolean;
  children: ReactNode;
}

export const BaseModal = forwardRef<HTMLDivElement, BaseModalProps>(
  function BaseModal(
    {
      isOpen,
      onClose,
      preventClose = false,
      mobileVariant = 'fullscreen',
      maxWidth = 'max-w-md',
      ariaLabel,
      onExitComplete,
      flushTop = false,
      children,
    },
    ref,
  ) {
    const isMobile = useMediaQuery(MD_BREAKPOINT);
    const sidebarWidth = useSidebarWidth(isMobile);
    const shellRef = useRef<HTMLDivElement>(null);
    const dragY = useMotionValue(0);
    // Backdrop opacity tracks drag position: full shade at top, fades as modal slides down
    const backdropOpacity = useTransform(dragY, [0, 600], [1, 0], { clamp: true });
    const isRTL =
      typeof document !== 'undefined'
        ? document.documentElement.dir === 'rtl'
        : false;

    // Merge forwarded ref with internal shellRef
    const setRefs = useCallback(
      (el: HTMLDivElement | null) => {
        shellRef.current = el;
        if (typeof ref === 'function') ref(el);
        else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = el;
      },
      [ref],
    );

    // Reset dragY when modal opens
    useEffect(() => {
      if (isOpen) dragY.set(0);
    }, [isOpen, dragY]);

    // Body scroll lock
    useEffect(() => {
      if (!isOpen) return;
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }, [isOpen]);

    // Track whether an animated exit is already running
    const isExiting = useRef(false);

    // Animated close: slide modal off-screen then unmount
    const animatedClose = useCallback(() => {
      if (preventClose || isExiting.current) return;
      if (!isMobile) { onClose(); return; }
      isExiting.current = true;
      const height = shellRef.current?.getBoundingClientRect().height || window.innerHeight;
      dragY.stop();
      animate(dragY, height + 20, {
        ...EXIT_SPRING,
        onComplete: () => {
          isExiting.current = false;
          onClose();
        },
      });
    }, [onClose, preventClose, dragY, isMobile]);

    // ESC key handler
    const handleKeyDown = useCallback(
      (e: KeyboardEvent) => {
        if (e.key === 'Escape') animatedClose();
      },
      [animatedClose],
    );

    useEffect(() => {
      if (!isOpen) return;
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, handleKeyDown]);

    const handleBackdropClick = preventClose ? undefined : animatedClose;

    // Drag-end handler: "shock absorber" dismiss or snap back
    const handleDragEnd = useCallback(
      (_: unknown, info: { offset: { y: number }; velocity: { y: number } }) => {
        const height = shellRef.current?.getBoundingClientRect().height || 0;
        const isMovingDown = info.velocity.y > 0;
        // Project where the finger was heading (offset + momentum lookahead)
        const projectedEndpoint = info.offset.y + info.velocity.y * 0.2;
        const shouldDismiss =
          !preventClose &&
          isMovingDown &&
          (projectedEndpoint > height * 0.5 || info.offset.y > height * 0.3);

        if (shouldDismiss) {
          // "Shock absorber" exit: animate to off-screen with velocity transfer,
          // then unmount. Higher damping = soft deceleration at the bottom.
          dragY.stop();
          animate(dragY, height + 20, {
            type: 'spring',
            stiffness: 250,
            damping: 35,
            velocity: info.velocity.y,
            onComplete: () => onClose(),
          });
        } else {
          // Snap back with velocity handover
          dragY.stop();
          animate(dragY, 0, { ...NATIVE_SPRING, velocity: info.velocity.y });
        }
      },
      [onClose, preventClose, dragY],
    );

    return createPortal(
      <AnimatePresence onExitComplete={onExitComplete}>
        {/* Backdrop */}
        {isOpen && (
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className={`fixed inset-0 ${isMobile ? '' : 'bg-black/60 backdrop-blur-sm'}`}
            style={{
              zIndex: isMobile ? Z_INDEX.mobileBackdrop : Z_INDEX.desktopBackdrop,
              ...(isMobile ? { backgroundColor: 'rgba(0,0,0,0.15)', opacity: backdropOpacity } : {}),
            }}
            onClick={handleBackdropClick}
            aria-hidden="true"
          />
        )}

        {/* Shell */}
        {isOpen && isMobile && mobileVariant === 'fullscreen' && (
          <motion.div
            key="fullscreen"
            initial={{ y: '100%' }}
            animate={{ y: 0, transition: SPRING.drawer }}
            exit={{ y: '100%', transition: EXIT_SPRING }}
            className="fixed inset-0 overflow-hidden"
            style={{ zIndex: Z_INDEX.mobileModal }}
          >
            <motion.div
              ref={setRefs}
              style={{ y: dragY, height: '100dvh', borderRadius: 0, paddingTop: flushTop ? undefined : (platform.isAndroid ? '16px' : 'calc(env(safe-area-inset-top, 0px) + 16px)') }}
              drag="y"
              dragConstraints={{ top: 0, bottom: 0 }}
              dragElastic={{ top: 0.1, bottom: 1 }}
              dragMomentum={false}
              onDragEnd={handleDragEnd}
              className="flex flex-col overflow-hidden h-full"
              dir={isRTL ? 'rtl' : 'ltr'}
              role="dialog"
              aria-modal="true"
              aria-label={ariaLabel}
            >
              <AnimatedGradientBackground />
              <ModalCloseContext.Provider value={animatedClose}>
                {children}
              </ModalCloseContext.Provider>
            </motion.div>
          </motion.div>
        )}

        {isOpen && isMobile && mobileVariant === 'bottomsheet' && (
          <motion.div
            key="bottomsheet"
            initial={{ y: '100%' }}
            animate={{ y: 0, transition: SPRING.bottomSheet }}
            exit={{ y: '100%', transition: EXIT_SPRING }}
            className="fixed bottom-0 left-0 right-0 overflow-hidden rounded-t-3xl"
            style={{ zIndex: Z_INDEX.mobileModal }}
          >
            <motion.div
              ref={setRefs}
              style={{ y: dragY, maxHeight: '90dvh' }}
              drag="y"
              dragConstraints={{ top: 0, bottom: 0 }}
              dragElastic={{ top: 0.1, bottom: 1 }}
              dragMomentum={false}
              onDragEnd={handleDragEnd}
              className="flex flex-col overflow-hidden pb-safe bg-white"
              dir={isRTL ? 'rtl' : 'ltr'}
              role="dialog"
              aria-modal="true"
              aria-label={ariaLabel}
            >
              <ModalCloseContext.Provider value={animatedClose}>
                {children}
              </ModalCloseContext.Provider>
            </motion.div>
          </motion.div>
        )}

        {isOpen && !isMobile && (
          <motion.div
            key="desktop"
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-4 flex items-start justify-center pointer-events-none pt-4"
            style={{
              zIndex: Z_INDEX.desktopModal,
              left: `calc(${sidebarWidth} + 1rem)`,
              top: `calc(var(--view-as-offset, 0px) + 1rem)`,
            }}
          >
            <div
              ref={setRefs}
              className={`w-full ${maxWidth} max-h-[90dvh] overflow-hidden flex flex-col pointer-events-auto glass-surface-modal`}
              dir={isRTL ? 'rtl' : 'ltr'}
              role="dialog"
              aria-modal="true"
              aria-label={ariaLabel}
            >
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>,
      document.body,
    );
  },
);
